import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  BookOpenIcon, 
  ShareIcon, 
  ChartBarIcon, 
  ShieldCheckIcon,
  SparklesIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const Landing = () => {
  const features = [
    {
      icon: BookOpenIcon,
      title: 'Rich Text Journaling',
      description: 'Write with a beautiful editor that supports formatting, images, and more.',
      color: 'text-primary-600',
      bgColor: 'bg-primary-50',
    },
    {
      icon: ShareIcon,
      title: 'Secure Sharing',
      description: 'Share your thoughts securely with time-limited access and permissions.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      icon: ChartBarIcon,
      title: 'Mood Analytics',
      description: 'Track your emotional journey with beautiful charts and insights.',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Privacy First',
      description: 'Your thoughts are yours. We use end-to-end encryption.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Writer',
      content: 'DiaryVerse has transformed how I journal. The mood tracking helps me understand my patterns.',
      avatar: '👩‍💼',
    },
    {
      name: 'Mike Chen',
      role: 'Student',
      content: 'The sharing feature is perfect for my therapy sessions. My therapist can access specific entries.',
      avatar: '👨‍🎓',
    },
    {
      name: 'Emma Davis',
      role: 'Artist',
      content: 'Beautiful design and smooth experience. It feels like writing in a premium journal.',
      avatar: '👩‍🎨',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-paper-white to-primary-100">
      {/* Navigation */}
      <nav className="glass-effect fixed w-full top-0 z-50 border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div 
              className="flex items-center space-x-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <BookOpenIcon className="h-8 w-8 text-primary-600" />
              <span className="text-2xl font-serif font-bold text-leather-brown">
                DiaryVerse
              </span>
            </motion.div>
            
            <motion.div 
              className="flex items-center space-x-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Link 
                to="/login" 
                className="text-ink-black hover:text-primary-600 font-medium transition-colors"
              >
                Sign In
              </Link>
              <Link to="/register" className="btn-primary">
                Get Started
              </Link>
            </motion.div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-serif font-bold text-ink-black mb-6">
              Your Digital
              <span className="text-gradient block">Journal</span>
              <span className="text-ink-black">Reimagined</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Capture your thoughts, track your moods, and share your journey with 
              the most beautiful and secure journaling platform ever created.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/register" className="btn-primary text-lg px-8 py-4">
                Start Writing Today
                <SparklesIcon className="ml-2 h-5 w-5 inline" />
              </Link>
              <Link to="/login" className="btn-secondary text-lg px-8 py-4">
                Sign In
              </Link>
            </div>
          </motion.div>

          {/* Hero Image/Animation */}
          <motion.div
            className="mt-16 relative"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <div className="relative mx-auto max-w-4xl">
              <div className="card p-8 transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <div className="diary-paper p-6 rounded-lg">
                  <h3 className="font-serif text-2xl text-ink-black mb-4">
                    Today's Entry
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    "The sunset painted the sky in shades of orange and pink today. 
                    It reminded me of the beauty in simple moments..."
                  </p>
                  <div className="flex items-center justify-between mt-6">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">😊</span>
                      <span className="text-sm text-gray-500">Happy</span>
                    </div>
                    <div className="flex space-x-2">
                      <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-xs">
                        gratitude
                      </span>
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                        nature
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating elements */}
              <motion.div
                className="absolute -top-4 -right-4 bg-gold-accent rounded-full p-3 shadow-lg"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <HeartIcon className="h-6 w-6 text-white" />
              </motion.div>
              
              <motion.div
                className="absolute -bottom-4 -left-4 bg-primary-500 rounded-full p-3 shadow-lg"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2.5, repeat: Infinity }}
              >
                <SparklesIcon className="h-6 w-6 text-white" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-ink-black mb-4">
              Everything You Need to
              <span className="text-gradient"> Journal Better</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful features designed to make journaling a delightful daily habit
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="card p-6 text-center group hover:scale-105"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`${feature.bgColor} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={`h-8 w-8 ${feature.color}`} />
                </div>
                <h3 className="text-xl font-semibold text-ink-black mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-r from-primary-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-ink-black mb-4">
              Loved by Writers
              <span className="text-gradient"> Everywhere</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                className="card p-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-3">{testimonial.avatar}</span>
                  <div>
                    <h4 className="font-semibold text-ink-black">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic">"{testimonial.content}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-ink-black text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
              Ready to Start Your
              <span className="text-gradient"> Journey?</span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of writers who have made DiaryVerse their digital sanctuary
            </p>
            <Link to="/register" className="btn-primary text-lg px-8 py-4 inline-flex items-center">
              Create Your Free Account
              <SparklesIcon className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <BookOpenIcon className="h-8 w-8 text-primary-400" />
              <span className="text-2xl font-serif font-bold">DiaryVerse</span>
            </div>
            <div className="flex space-x-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Terms</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Support</a>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 DiaryVerse. Made with ❤️ for writers everywhere.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
